import React from 'react';
import { RotateCcw, CheckCircle, XCircle, Package, Clock } from 'lucide-react';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-sky-50 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 
            className="text-4xl font-bold text-slate-800 mb-4"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
          >
            Refund & Return Policy
          </h1>
          <p className="text-slate-600">Last updated: January 2026</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Quick Summary */}
        <div className="bg-sky-50 rounded-2xl p-6 mb-12">
          <h2 
            className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            <RotateCcw className="text-sky-500" size={24} />
            30-Day Hassle-Free Returns
          </h2>
          <p className="text-slate-600" style={{ fontFamily: 'Quicksand, sans-serif' }}>
            We want you and your children to be completely satisfied with your purchase. If for any reason you're not happy, we offer a simple 30-day return policy.
          </p>
        </div>

        <div className="prose prose-slate max-w-none" style={{ fontFamily: 'Quicksand, sans-serif' }}>
          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            1. Return Eligibility
          </h2>
          <p className="text-slate-600 mb-4">To be eligible for a return, items must meet the following conditions:</p>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-green-50 rounded-xl p-4">
              <h3 className="font-bold text-green-700 flex items-center gap-2 mb-3">
                <CheckCircle size={20} />
                Eligible for Return
              </h3>
              <ul className="text-slate-600 space-y-2 text-sm">
                <li>• Within 30 days of delivery</li>
                <li>• Unused and in original condition</li>
                <li>• In original packaging</li>
                <li>• With all tags and accessories</li>
                <li>• Proof of purchase available</li>
              </ul>
            </div>
            <div className="bg-red-50 rounded-xl p-4">
              <h3 className="font-bold text-red-700 flex items-center gap-2 mb-3">
                <XCircle size={20} />
                Not Eligible for Return
              </h3>
              <ul className="text-slate-600 space-y-2 text-sm">
                <li>• Items past 30-day window</li>
                <li>• Used, damaged, or modified items</li>
                <li>• Items without original packaging</li>
                <li>• Clearance or final sale items</li>
                <li>• Gift cards</li>
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            2. How to Initiate a Return
          </h2>
          <p className="text-slate-600 mb-4">To start a return, please follow these steps:</p>
          <ol className="list-decimal pl-6 text-slate-600 mb-6 space-y-3">
            <li><strong>Contact Us:</strong> Email us at support@cartlixs.store or call +1-888-435-6822 with your order number and reason for return.</li>
            <li><strong>Receive Authorization:</strong> We'll send you a Return Merchandise Authorization (RMA) number and return instructions within 24-48 hours.</li>
            <li><strong>Pack Your Item:</strong> Securely pack the item in its original packaging with all accessories and tags.</li>
            <li><strong>Ship the Return:</strong> Send the package to our return address provided with your RMA. We recommend using a trackable shipping method.</li>
            <li><strong>Receive Refund:</strong> Once we receive and inspect your return, we'll process your refund within 5-7 business days.</li>
          </ol>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            3. Refund Process
          </h2>
          <div className="bg-slate-50 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="text-sky-500" size={24} />
              <h3 className="font-bold text-slate-800">Refund Timeline</h3>
            </div>
            <ul className="text-slate-600 space-y-2">
              <li>• <strong>Inspection:</strong> 1-2 business days after receiving your return</li>
              <li>• <strong>Refund Processing:</strong> 3-5 business days after approval</li>
              <li>• <strong>Bank Processing:</strong> 5-10 business days to appear in your account</li>
            </ul>
          </div>
          <p className="text-slate-600 mb-6">
            Refunds will be credited to your original payment method. For Cash on Delivery orders, refunds will be processed via bank transfer or store credit (your choice).
          </p>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            4. Return Shipping Costs
          </h2>
          <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
            <li><strong>Defective or Wrong Items:</strong> We cover return shipping costs and will send a prepaid label.</li>
            <li><strong>Change of Mind:</strong> Customer is responsible for return shipping costs.</li>
            <li><strong>Free Shipping Threshold:</strong> Original shipping charges may be deducted if your return brings the order below our free shipping minimum ($50).</li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            5. Exchanges
          </h2>
          <p className="text-slate-600 mb-6">
            We currently do not offer direct exchanges. If you'd like a different item, please return your original purchase for a refund and place a new order. This ensures the fastest processing time.
          </p>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            6. Damaged or Defective Items
          </h2>
          <p className="text-slate-600 mb-6">
            If you receive a damaged or defective item, please contact us within 48 hours of delivery with photos of the damage. We'll arrange for a replacement or full refund, including shipping costs.
          </p>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            7. Lost or Missing Packages
          </h2>
          <p className="text-slate-600 mb-6">
            If your package is marked as delivered but you haven't received it, please contact us within 7 days. We'll work with the shipping carrier to locate your package or issue a replacement/refund.
          </p>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            8. Contact Us
          </h2>
          <p className="text-slate-600 mb-4">
            For any questions about returns or refunds, our customer service team is here to help:
          </p>
          <div className="bg-slate-50 rounded-xl p-6">
            <ul className="text-slate-600 space-y-2">
              <li><strong>Email:</strong> support@cartlixs.store</li>
              <li><strong>Phone:</strong> +1-888-435-6822</li>
              <li><strong>Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM EST</li>
              <li><strong>Address:</strong> 192 A 22nd St #4699, New York, NY 30272</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
